import { MealPlanDisplayFoodItemFragment, MealPlanShefFragment } from 'src/gqlReactTypings.generated.d';
import { useMealPlanCatalogStore } from '../meal-plan-catalog-store';

interface MealPlanSegmentLike {
  foodItems: MealPlanDisplayFoodItemFragment[];
  shef: MealPlanShefFragment;
}

/** Minimal shape for collections whose dishes match the MealPlanDisplayFoodItem fragment. */
interface CatalogCollection {
  categories: ReadonlyArray<{ dishes: ReadonlyArray<MealPlanDisplayFoodItemFragment> }>;
}

/**
 * Populates the catalog store from shef segment data (mains/sides).
 * Clears stale data when the delivery date has changed so that skip-if-exists
 * setters don't preserve availability values from a previous date.
 * Safe to call from onCompleted callbacks — uses getState() to avoid hook constraints.
 */
export function populateCatalogFromSegments(segments: MealPlanSegmentLike[], deliveryDate: string): void {
  const store = useMealPlanCatalogStore.getState();
  if (store.populatedForDeliveryDate !== null && store.populatedForDeliveryDate !== deliveryDate) {
    store.clear();
  }
  const { bulkSetFoodItems, bulkSetShefs, markPopulated } = useMealPlanCatalogStore.getState();
  const allFoodItems: MealPlanDisplayFoodItemFragment[] = [];
  const allShefs: MealPlanShefFragment[] = [];
  for (const segment of segments) {
    allFoodItems.push(...segment.foodItems);
    allShefs.push(segment.shef);
  }
  bulkSetFoodItems(allFoodItems);
  bulkSetShefs(allShefs);
  markPopulated(deliveryDate);
}

/**
 * Populates the catalog store with food items from ForYou collection data.
 * Clears stale data when the delivery date has changed so that skip-if-exists
 * setters don't preserve availability values from a previous date.
 * Does not set shefs — collections don't include full MealPlanShefFragment data.
 * Safe to call from onCompleted callbacks — uses getState() to avoid hook constraints.
 */
export function populateCatalogFoodItemsFromCollections(
  collections: ReadonlyArray<CatalogCollection>,
  deliveryDate: string
): void {
  const store = useMealPlanCatalogStore.getState();
  if (store.populatedForDeliveryDate !== null && store.populatedForDeliveryDate !== deliveryDate) {
    store.clear();
  }
  const { bulkSetFoodItems, markPopulated } = useMealPlanCatalogStore.getState();
  const allFoodItems: MealPlanDisplayFoodItemFragment[] = [];
  for (const collection of collections) {
    for (const category of collection.categories) {
      allFoodItems.push(...category.dishes);
    }
  }
  bulkSetFoodItems(allFoodItems);
  markPopulated(deliveryDate);
}

export function useMealPlanCatalogLookup(
  foodItemId: number,
  shefId: string
): { foodItem: MealPlanDisplayFoodItemFragment | undefined; shef: MealPlanShefFragment | undefined } {
  const foodItem = useMealPlanCatalogStore((s) => s.foodItems[foodItemId]);
  const shef = useMealPlanCatalogStore((s) => s.shefs[shefId]);
  return { foodItem, shef };
}

/** True once every `foodItemIds` entry is present in the catalog. */
export function useCatalogHasAllFoodItems(foodItemIds: number[]): boolean {
  return useMealPlanCatalogStore((s) => foodItemIds.every((id) => !!s.foodItems[id]));
}

export function useCatalogHasAllDishesAndShefs(dishes: ReadonlyArray<{ id: number; shef: { id: string } }>): boolean {
  return useMealPlanCatalogStore((s) => dishes.every((dish) => !!s.foodItems[dish.id] && !!s.shefs[dish.shef.id]));
}
